import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import MainBody from '../../components/layout/MainBody';
import Section from '../../components/layout/Section';
import FormContainer from '../../components/UI/FormContainer';
import { FormTitle, Field, AddressField } from '../../components/UI/Form';
import Button from '../../components/UI/Button';
import { useCheck } from '../../hooks/useCheck';
import { userApi } from '../../api/user';

const Container = styled.div`
	width: 90%;
	max-width: 900px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 30px;
`;

export default function SignUp() {
	const [formData, setFormData] = useState({
		id: '',
		password: '',
		confirmPassword: '',
		email: '',
		name: '',
		contact: '',
		postalCode: '',
		roadAddress: '',
		detailAddress: ''
	});

	const navigate = useNavigate();

	const idCheck = useCheck(userApi.checkDuplicateId);
	const emailCheck = useCheck(userApi.checkDuplicateEmail); 

	const { id, password, confirmPassword, email, name, contact, postalCode, roadAddress, detailAddress } = formData;

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});

		if (e.target.name === 'id') {
			idCheck.reset();
		}
		if (e.target.name === 'email') {
			emailCheck.reset();
		}
	};

	const handleChangeAddress = ({postalCode, roadAddress}) => {
		setFormData(prev => ({
			...prev,
			postalCode,
			roadAddress
		}));
	};

	const handleCheckId = async () => {
		if(!id) {
			alert('아이디를 입력해 주세요.');
			return;
		}

		// 아이디 유효성 검사
		if(id.length > 20) {
			alert('아이디는 20자 이내로 입력해 주세요.');
			return;
		}

		// 아이디 유효성 검사 - 영문, 숫자만 허용
		const idRegex = /^[a-zA-Z0-9]+$/;	
		if(!idRegex.test(id)) {
			alert('아이디는 영문 및 숫자만 사용할 수 있습니다.');
			return;
		}

		const isAvailable = await idCheck.checkValue(id);

		if(isAvailable) {
			alert('사용 가능한 아이디입니다.');
		} else {
			alert('이미 사용 중인 아이디입니다.');
		}
	};

	const handleCheckEmail = async () => {
		if(!email) {
			alert('이메일을 입력해 주세요.');
			return;
		}
		
		// 이메일 유효성 검사
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if(!emailRegex.test(email)) {
			alert('유효한 이메일 주소를 입력해 주세요.');
			return;
		}

		const isAvailable = await emailCheck.checkValue(email);

		if(isAvailable) {
			alert('사용 가능한 이메일입니다.');
		} else {
			alert('이미 사용 중인 이메일입니다.');
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if(!idCheck.isAvailable) {
			alert('아이디 중복확인을 해주세요.');
			return;
		}

		if(!emailCheck.isAvailable) {
			alert('이메일 중복확인을 해주세요.');
			return;
		}

		// 비밀번호 유효성 검사 - 영문, 숫자 조합 5자 이상
		const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{5,}$/;
		if(!passwordRegex.test(password)) {
			alert('비밀번호는 영문과 숫자 조합으로 5자 이상이어야 합니다.');
			return;
		}

		if(password !== confirmPassword) {
			alert('비밀번호가 일치하지 않습니다.');
			return;
		}

		// 연락처 유효성 검사 - 숫자만 허용
		const contactRegex = /^\d+$/;
		if(!contactRegex.test(contact)) {
			alert('연락처는 숫자만 입력해 주세요.');
			return;
		}

		if(!name || !contact || !postalCode || !roadAddress || !detailAddress) {
			alert('모든 필드를 입력해 주세요.');
			return;
		}	

		// api 호출
		userApi.signUp(formData)
			.then(response => {
				if(response.id) {
					alert('회원가입이 완료되었습니다.');
					navigate('/log-in');
					return;
				}
				alert('회원가입에 실패했습니다. 다시 시도해주세요.');
			})
			.catch(error => {
				console.error('Sign Up Error:', error);
				alert('회원가입 중 오류가 발생했습니다. 다시 시도해주세요.');
			});
	};

  	return (
		<MainBody>
			<Section>
				<Container>
					<FormContainer style={{ border: 'none' }}>
						<FormTitle style={{ marginBottom: '30px' }}>회원가입</FormTitle>
						<Field title="아이디" name="id" value={id} type="button" $placeholder="영문 및 숫자" buttonText="중복확인"
							editable={ true }
							onClick={ handleCheckId } onHandleChange={ handleChange }
						/>
						<Field title="비밀번호" name="password" value={password}  type="password" $placeholder="영문, 숫자 조합 5자 이상" onHandleChange={ handleChange }/>
						<Field title="비밀번호 확인" name="confirmPassword" value={confirmPassword} type="password" $placeholder="비밀번호 재입력" onHandleChange={ handleChange }/>
						<Field title="이메일" name="email" value={email} type="button" $placeholder="이메일" buttonText="중복확인"
							editable={ true }
							onClick={ handleCheckEmail } onHandleChange={ handleChange }
						/>
						<Field title="이름" name="name" value={name} type="text" $placeholder="이름" onHandleChange={ handleChange }/>
						<Field title="연락처" name="contact" value={contact} type="tel" $placeholder='"-" 없이 입력해 주세요.' onHandleChange={ handleChange }/>
						<AddressField value={{ postalCode, roadAddress, detailAddress }}
							onHandleChange={ handleChange } onAddressComplete={ handleChangeAddress }
						/>
					</FormContainer>
					<Button buttonText="가입하기" width="400px" onClick={ handleSubmit } />
				</Container>
			</Section>
		</MainBody>
  	);
}
